import React, { useMemo } from "react";
import { Box, Text, Flex, Anchor, Badge } from "@mantine/core";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  useMantineReactTable,
} from "mantine-react-table";
import {
  IconFolder,
  IconFolderOpen,
  IconFile,
  IconStar,
  IconStarOff,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";
import { _localization } from "../../../config/location";
import { CategoryModelTreeNew } from "../../../model/TblCategoryModel";

interface CategoryTreeTableProps {
  categoryTree: CategoryModelTreeNew[];
  loadingCategoryTree: boolean;
  categoryPagination: MRT_PaginationState;
  setCategoryPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  categoryRowCount: number;
  onSelectCategory: (catId: number) => void;
}

export const CategoryTreeTable: React.FC<CategoryTreeTableProps> = ({
  categoryTree,
  loadingCategoryTree,
  categoryPagination,
  setCategoryPagination,
  categoryRowCount,
  onSelectCategory,
}) => {
  const categoryColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "STT",
        header: "STT",
        enableSorting: false,
        size: 110,
        Cell: ({ row }: any) => {
          const getFormattedIndex = (r: any): string => {
            let index = r.index + 1;
            let parent = r.getParentRow?.();
            let formatted = `${index}`;
            while (parent) {
              formatted = `${parent.index + 1}.${formatted}`;
              parent = parent.getParentRow?.();
            }
            return formatted;
          };

          const getIconByDepth = (depth: number) => {
            const size = 16;
            switch (depth) {
              case 0:
                return <IconFolder size={size} color="#333" />;
              case 1:
                return <IconFolderOpen size={size} color="#4c6ef5" />;
              case 2:
                return <IconFile size={size} color="#38d9a9" />;
              default:
                return <IconFile size={size} color="#f76707" />;
            }
          };

          return (
            <Flex align="center" gap="xs">
              {getIconByDepth(row.depth)}
              <span style={{ fontWeight: 500, fontSize: 13 }}>
                {getFormattedIndex(row)}
              </span>
            </Flex>
          );
        },
      },
      {
        accessorKey: "categoryName",
        header: "Tên danh mục",
        size: 300,
        Cell: ({ renderedCellValue }: any) => (
          <Text fw={500} size="sm">
            {renderedCellValue}
          </Text>
        ),
      },
      {
        accessorKey: "priorityStatus",
        header: "Nổi bật",
        size: 130,
        Cell: ({ row }: any) => {
          const isPriority = row.original.priorityStatus === "Y";
          return (
            <Flex align="center" gap="xs">
              {isPriority ? (
                <IconStar size={16} color="blue" />
              ) : (
                <IconStarOff size={16} color="red" />
              )}
              <Text c={isPriority ? "blue" : "red"} fw={500} size="xs">
                {isPriority ? "Hạ nổi bật" : "Cho nổi bật"}
              </Text>
            </Flex>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 130,
        Cell: ({ row }: any) => {
          const isActive = row.original.status === "A";
          return (
            <Flex align="center" gap="xs">
              {isActive ? (
                <IconEye size={16} color="green" />
              ) : (
                <IconEyeOff size={16} color="gray" />
              )}
              <Text c={isActive ? "green" : "gray"} fw={500} size="xs">
                {isActive ? "Hiển thị" : "Không hiển thị"}
              </Text>
            </Flex>
          );
        },
      },
      {
        accessorKey: "id",
        header: "Id",
        size: 90,
        Cell: ({ row }: any) => <Text size="sm">{row.original.id}</Text>,
      },
      {
        accessorKey: "visit",
        header: "Xem",
        size: 90,
        Cell: ({ row }: any) => <Text size="sm">{row.original.visit ?? ""}</Text>,
      },
      {
        accessorKey: "itemCount",
        header: "SP",
        size: 90,
        Cell: ({ row }: any) => (
          <Badge color="gray" variant="light">
            {row.original.itemCount ?? 0}
          </Badge>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        size: 240,
        Cell: ({ row }: any) => (
          <Anchor
            component="button"
            type="button"
            underline="hover"
            fw={600}
            c="blue"
            size="sm"
            onClick={() => onSelectCategory(row.original.id)}
          >
            Xem sản phẩm chưa có thuộc tính
          </Anchor>
        ),
      },
    ],
    [onSelectCategory]
  );

  const categoryTable = useMantineReactTable({
    columns: categoryColumns,
    data: (categoryTree || []) as any[],
    enableExpanding: true,
    getSubRows: (row: any) => row.categoryChildModels || [],
    enableColumnResizing: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    localization: _localization,
    manualPagination: true,
    rowCount: categoryRowCount,
    onPaginationChange: setCategoryPagination,
    layoutMode: "semantic",
    mantineTableProps: {
      style: {
        width: "100%",
      },
    },
    initialState: {
      density: "xs",
      expanded: {}, // Collapsed by default
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      rowsPerPageOptions: ["20", "50", "100", "500"],
    },
    paginationDisplayMode: "pages",
    mantineTableBodyCellProps: ({ row, cell }: any) => {
      const indentPx = row.depth * 20;
      const targetColumns = ["STT", "categoryName"];
      return {
        style: {
          paddingLeft: targetColumns.includes(cell.column.id)
            ? `${indentPx + 8}px`
            : "8px",
        },
      };
    },
    mantineTableBodyRowProps: ({ row }: any) => ({
      style: {
        backgroundColor: row.depth === 0 ? "#f9f9f9" : "#fff",
      },
    }),
    getRowId: (row: any) => row.id?.toString(),
    state: {
      isLoading: loadingCategoryTree,
      pagination: categoryPagination,
    },
  });

  return (
    <Box style={{ position: "relative", overflowX: "auto" }}>
      <MantineReactTable table={categoryTable} />
    </Box>
  );
};
